// Import dependencies
import { unsafeCSS, css, html, render, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

// Import style for web component
import styles from './web-component-style.scss';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-unsupported-notice' )
export class AutomatorUnsupportedNotice extends LitElement {
	
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;
	
	/**
	 * The current recipe item ( action or trigger )
	 *
	 * @type {item} The recipe item
	 */
	@property( { type: Object, attribute: 'item' } ) accessor item;
	
	/**
	 * The custom message config
	 * 
	 * @type {Object}
	 */
	config = {
		key: '',
		args: [],
		url: false
	};
	
	/**
	 * Render unsupported message.
	 *
	 * @return {TemplateResult} The template result
	 */
	render() {
		
		// Set the default message.
		let message = _uo.utility.escHTML( wp.i18n.__( 'This item was disabled because it could not be found on the system. To re-enable, ensure the associated plugin is installed and activated.', 'uncanny-automator' ) );
		
		// Check if this is a permalink issue.
		if ( UncannyAutomator._site.permalink_structure === '' ) {
			message = _uo.utility.escHTML( wp.i18n.__( 'This item was disabled because your Permalinks are not set correctly.', 'uncanny-automator' ) );
		}
		
		// Determine the custom message.
		this._setCustomMessageConfig();
		
		// Validate the message key.
		if ( this._i18nKeyIsValid() ) {
			// Render the custom message content.
			message = this._renderCustomMessage();
		}
		
		return html`
			<uo-alert type="warning">
				${message}
			</uo-alert>
		`;
	}
	
	/**
	 * Render the unsupported/unavailable message
	 * 
	 * @return {TemplateResult} The template result
	 */
	_renderCustomMessage() {
		// Get the i18n config by key.
		const i18n = this.strings[ this.config.key ];
		// Get the message with render args.
		const message = _uo.utility.sprintf( i18n.message, ...this.config.args );
		// Get the url for the button.
		const href = this._isValid( this.config.url ) ? this.config.url : i18n.url;
		// Render the HTML
		return html`
			${ message }
			<uo-button
				href="${ href }"
				target="_blank"
				type="link"
				size="extra-small"
			>
				${ i18n.button }
			</uo-button>
		`;
	}
	
	/**
	 * Set the message config for the unsupported/unavailable item.
	 * 
	 * @return void
	 */
	_setCustomMessageConfig() {
		
		// Get the item data
		const {
			integrationCode,
			itemType,
			meta
		} = this.item;
		
		// Item type is 'free', 'pro' or 'elite'
		const itemMetaType = this._isValid( meta.type ) ? meta.type : false;
		
		// Integration data.
		const integration = UncannyAutomator._integrations.hasOwnProperty( integrationCode )
			? UncannyAutomator._integrations[ integrationCode ]
			: false;
			
		// Bail, we don't have available info. Return for the default message.
		if ( _uo.utility.isEmpty( integration ) || _uo.utility.isEmpty( itemMetaType ) ) {
			return;
		}
		
		// Item type.
		const isFreeItem   = itemMetaType === 'free';
		const isProItem    = itemMetaType === 'pro';
		const isEliteItem  = itemMetaType === 'elite';
		
		// Connection status.
		const { 
			has_account_connected,
			has_valid_pro_license
		} = UncannyAutomator._site.automator;
		
		// Plugin active status.
		const isAvailable = this._isValid( integration.is_available ) ? integration.is_available : false;
		
		// Set item type args.
		this.config.args = [ itemType ];

		// Get the URL
		const goToLinks = {
			freeConnection: UncannyAutomator._site.automator.links.manage_license,
			proLicense: UncannyAutomator._site.automator.links.manage_license,
			proRequired: UncannyAutomatorBackend._site.automator.links.external.url_upgrade_to_pro,
			pluginRequired: UncannyAutomator._site.automator.links.plugin_required_missing,
		}
		
		// App specific messaging.
		if ( this._isValid( integration.is_app ) ) {
			// Free account not connected.
			if ( isFreeItem && ! has_account_connected ) {
				this.config.key = 'freeConnection';
				this.config.url = goToLinks.freeConnection;
				return;
			}
			
			// Pro item and no pro license key entered.
			if ( isProItem && ! has_valid_pro_license ) {
				this.config.key = ! UncannyAutomatorBackend._site.automator.is_pro_active
					? 'proRequired' // Pro not installed / activated.
					: 'proLicense'; // Pro license key not entered.
				this.config.url = goToLinks.proLicense;
				return;
			}
			
			// App integration not available because app not connected.
			const misc = this._isValid( integration.miscellaneous ) ? integration.miscellaneous : {};
			const connected = this._isValid( misc.is_app_connected ) ? misc.is_app_connected : false;
			if ( 'success' !== connected && this._isValid( misc.url_settings_page ) ) {
				this.config.key = 'appConnection';
				this.config.url = misc.url_settings_page;
				return;
			}
			
			// No message found return for the default message.
			return;
		}
		
		// Trigger or action messaging.
		if ( itemType === 'trigger' || itemType === 'action' ) {
			// Trigger/action not available in free plugin
			if ( isProItem && ! UncannyAutomatorBackend._site.automator.is_pro_active ) {
				this.config.key = 'proRequired';
				this.config.url = goToLinks.proRequired;
				return;
			}
			
			// Trigger/action not available because plugin not active
			if ( ! isAvailable ) {
				this.config.key = 'pluginRequired';
				this.config.args = [ itemType, integration.name ];
				this.config.url = goToLinks.pluginRequired;
				return;
			}
		}
	}

	/**
	 * Check if the value is defined and not empty
	 * 
	 * @param {Mixed} value
	 * 
	 * @return {Boolean}
	 */
	_isValid( value ){
		return _uo.utility.isDefined( value ) && ! _uo.utility.isEmpty( value );
	}

	/**
	 * Check if the i18n key is valid
	 * 
	 * @return {Boolean}
	 */
	_i18nKeyIsValid() {
		return this._isValid( this.config.key ) 
			&& this.strings.hasOwnProperty( this.config.key );
	}

	/**
	 * Get the strings for the unsupported/unavailable message
	 * 
	 * @return {Object}
	 */
	get strings() {
		return {
			freeConnection: {
				message: _uo.utility.escHTML( wp.i18n.__( 'Please connect your account to use this %s.', 'uncanny-automator' ) ),
				button: _uo.utility.escHTML( wp.i18n.__( 'Connect account', 'uncanny-automator' ) ),
			},
			proLicense: {
				/* translators: 1. Item type (action, trigger) */
				message: _uo.utility.escHTML( wp.i18n.__( 'Please enter your Automator Pro license key to use this %s.', 'uncanny-automator' ) ),
				button: _uo.utility.escHTML( wp.i18n.__( 'Enter license key', 'uncanny-automator' ) ),
			},
			proRequired: {
				/* translators: 1. Item type (action, trigger) */
				message: _uo.utility.escHTML( wp.i18n.__( 'Automator Pro is required to use this %s.', 'uncanny-automator' ) ),
				button: _uo.utility.escHTML( wp.i18n.__( 'Upgrade now', 'uncanny-automator' ) )
			},
			appConnection: {
				/* translators: 1. Item type (action, trigger) */
				message: _uo.utility.escHTML( wp.i18n.__( 'App integration not connected. Please connect to use this %s.', 'uncanny-automator' ) ),
				button: _uo.utility.escHTML( wp.i18n.__( 'Connect app', 'uncanny-automator' ) ),
			},
			pluginRequired: {
				/* translators: 1. Item type (action, trigger), 2. Plugin name */
				message: _uo.utility.escHTML( wp.i18n.__( 'This %1$s requires the %2$s plugin which is not currently activated.', 'uncanny-automator' ) ),
				button: _uo.utility.escHTML( wp.i18n.__( 'Learn more', 'uncanny-automator' ) )
			}
		}
	}
}